import { Box, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import React, { useCallback, useRef, useState } from 'react';

import { TDocument } from '@/types/document';

import NoRowsOverlay from '@/@mui/data-grid/NoRowsOverlay';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { DocumentStatusProvider } from '@/context/DocumentStatusProvider';

import DocumentCategoryCell from '@/components/document/DocumentCategoryCell';
import DocumentDeleteModal from '@/components/document/DocumentDeleteModal';
import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';

interface DropZoneProps {
  onFileDrop: (file: File) => void;
  isUploading: boolean;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDrop, isUploading, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (isUploading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        onFileDrop(file);
      }
    },
    [onFileDrop, isUploading]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&::after': isDragging
          ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 10,
            pointerEvents: 'none',
          }
          : {},
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 11,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <UploadCloud01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
            Drop file to upload
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface DocumentListLayoutProps {
  documents: TDocument[];
  columns: GridColDef[];
  isLoading: boolean;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  documentUploadButtonRef: React.RefObject<DocumentUploadButtonAPI>;
  isFileUploading: boolean;
  onFileSelect: (file: File) => void;
  onUploadDocumentClick: () => void;
  isDocumentDeleteModalOpen: boolean;
  documentToDelete: TDocument | null;
  onDocumentDeleteModalClose: () => void;
  onDocumentDeleted: () => void;
}

export const DocumentListLayout: React.FC<DocumentListLayoutProps> = ({
  documents,
  columns: baseColumns,
  isLoading,
  sortModel,
  onSortModelChange,
  documentUploadButtonRef,
  isFileUploading,
  onFileSelect,
  onUploadDocumentClick,
  isDocumentDeleteModalOpen,
  documentToDelete,
  onDocumentDeleteModalClose,
  onDocumentDeleted,
}) => {
  const DocumentListNoRowsOverlay = React.useCallback(
    () => (
      <NoRowsOverlay
        entityName="Document"
        addEntityButtonIcon={<UploadCloud01Icon />}
        addEntityButtonText="Upload Document"
        isLoading={isFileUploading}
        onAddEntityClick={onUploadDocumentClick}
      />
    ),
    [onUploadDocumentClick, isFileUploading]
  );

  const columns: GridColDef[] = React.useMemo(() => {
    const columnsWithoutActions = [...baseColumns];
    const actionsColumn = columnsWithoutActions.pop();

    const updatedColumns = [
      ...columnsWithoutActions,
      {
        field: 'category',
        headerName: 'Category',
        width: 200,
        renderCell: (params: GridRenderCellParams<TDocument>) => (
          <DocumentCategoryCell document={params.row} />
        ),
      },
    ];

    if (actionsColumn) {
      updatedColumns.push(actionsColumn);
    }

    return updatedColumns;
  }, [baseColumns]);

  return (
    <DocumentStatusProvider>
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
        <DocumentUploadButton
          ref={documentUploadButtonRef}
          isFileUploading={isFileUploading}
          onFileSelect={onFileSelect}
          sx={{
            position: { sm: 'absolute' },
            top: { sm: -54 },
            right: { sm: 0 },
          }}
        />

        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
          <DropZone onFileDrop={onFileSelect} isUploading={isFileUploading}>
            <DataGrid
              rows={documents}
              columns={columns}
              density="comfortable"
              disableColumnFilter
              disableColumnMenu
              disableEval
              loading={isLoading}
              slots={{
                noRowsOverlay: DocumentListNoRowsOverlay,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'skeleton',
                  noRowsVariant: 'skeleton',
                },
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              disableRowSelectionOnClick
            />
          </DropZone>
        </Box>

        {isDocumentDeleteModalOpen && documentToDelete && (
          <DocumentDeleteModal
            document={documentToDelete}
            onClose={onDocumentDeleteModalClose}
            onDocumentDeleted={onDocumentDeleted}
          />
        )}
      </Stack>
    </DocumentStatusProvider>
  );
};
